import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  useTheme,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import { SystemSection, useSystemsContext } from "../../context/SystemsContext";
import { useSession } from "../../context/SessionContext";

const tabOptions: SystemSection[] = [
  "assetProcessing",
  "assetSyndication",
  "assetStorage",
  "clientProcess",
];

const sectionLabel: Record<string, string> = {
  assetProcessing: "Asset Processing",
  assetSyndication: "Asset Syndication",
  assetStorage: "Asset Storage",
  clientProcess: "Client Process",
};

interface AccordionPanelProps {
  mobile?: boolean;
  onNavigate?: () => void;
}

const AccordionPanel: React.FC<AccordionPanelProps> = ({ mobile = false, onNavigate }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.status.accent;
  const { user } = useSession();
  const isAdmin = user?.role === "Admin";

  const {
    configs,
    selectedConfig: expandedConfigId,
    selectedSection,
    selectConfig,
    setSelectedSection,
    createSystem,
    createConfig,
    formData,
  } = useSystemsContext();

  // Mobile-phone systems have no rig hardware, so hide the System Hardware nav.
  const isMobileUnit = formData?.unit_type === "mobile";

  const [systemExpanded, setSystemExpanded] = useState(true);
  const [configExpanded, setConfigExpanded] = useState(true);

  const sectionAccordionSx: SxProps<Theme> = {
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:before": { display: "none" },
    m: 0,
  };

  const sectionSummarySx: SxProps<Theme> = {
    px: 2,
    minHeight: 44,
    "& .MuiAccordionSummary-content": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      my: 1,
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      color: "text.secondary",
    },
  };

  const sectionTitleSx: SxProps<Theme> = {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "text.secondary",
  };

  const navItemSx = (selected: boolean): SxProps<Theme> => ({
    mx: 1.25,
    my: 0.25,
    px: 1.5,
    py: 0.75,
    borderRadius: "8px",
    transition: "background-color 140ms ease, color 140ms ease",
    color: selected ? "text.primary" : "text.secondary",
    backgroundColor: selected ? alpha(accent, isDark ? 0.14 : 0.1) : "transparent",
    position: "relative",
    "&::before": selected
      ? {
          content: '""',
          position: "absolute",
          left: 4,
          top: 8,
          bottom: 8,
          width: 2,
          borderRadius: 1,
          backgroundColor: accent,
        }
      : undefined,
    "&:hover": {
      backgroundColor: selected
        ? alpha(accent, isDark ? 0.18 : 0.14)
        : alpha(isDark ? "#fff" : "#000", 0.04),
    },
    "&.Mui-selected": {
      backgroundColor: alpha(accent, isDark ? 0.14 : 0.1),
      "&:hover": {
        backgroundColor: alpha(accent, isDark ? 0.18 : 0.14),
      },
    },
  });

  const navItemTextSx = (selected: boolean) => ({
    fontWeight: selected ? 600 : 500,
    letterSpacing: "-0.005em",
  });

  return (
    <Box
      sx={{
        width: mobile ? "100%" : 288,
        flexShrink: 0,
        borderRight: mobile ? "none" : `1px solid ${alpha(isDark ? "#fff" : "#000", 0.06)}`,
        backgroundColor: isDark ? "#0e0e10" : "#fafafb",
        overflowY: "auto",
        py: 1.5,
      }}
    >
      {/* SYSTEM */}
      <Accordion
        expanded={systemExpanded}
        onChange={() => setSystemExpanded((prev) => !prev)}
        disableGutters
        square
        sx={sectionAccordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
          <Typography sx={sectionTitleSx}>System</Typography>
          <Tooltip title="Add system" arrow>
            <IconButton
              data-cy="system-add-system-btn"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                createSystem();
                onNavigate?.();
              }}
              sx={{
                ml: 1,
                color: "text.secondary",
                borderRadius: "8px",
                "&:hover": {
                  color: accent,
                  backgroundColor: alpha(accent, 0.1),
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, pb: 1 }}>
          <List disablePadding>
            <ListItemButton
              data-cy="system-nav-system-info"
              selected={selectedSection === "systemInfo"}
              onClick={() => {
                selectConfig(null);
                onNavigate?.();
              }}
              sx={navItemSx(selectedSection === "systemInfo")}
            >
              <ListItemText
                primaryTypographyProps={{
                  variant: "body2",
                  sx: navItemTextSx(selectedSection === "systemInfo"),
                }}
                primary="System Info"
              />
            </ListItemButton>
            {!isMobileUnit && (
              <ListItemButton
                data-cy="system-nav-system-hardware"
                selected={selectedSection === "systemHardware"}
                onClick={() => {
                  selectConfig(null);
                  setSelectedSection("systemHardware");
                  onNavigate?.();
                }}
                sx={navItemSx(selectedSection === "systemHardware")}
              >
                <ListItemText
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: navItemTextSx(selectedSection === "systemHardware"),
                  }}
                  primary="System Hardware"
                />
              </ListItemButton>
            )}
            {isAdmin && (
              <ListItemButton
                data-cy="system-nav-service-definitions"
                selected={selectedSection === "serviceDefinitions"}
                onClick={() => {
                  setSelectedSection("serviceDefinitions");
                  onNavigate?.();
                }}
                sx={navItemSx(selectedSection === "serviceDefinitions")}
              >
                <ListItemText
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: navItemTextSx(selectedSection === "serviceDefinitions"),
                  }}
                  primary="Service Definitions"
                />
              </ListItemButton>
            )}
          </List>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ height: 8 }} />

      {/* CONFIGURATIONS */}
      <Accordion
        expanded={configExpanded}
        onChange={() => setConfigExpanded((prev) => !prev)}
        disableGutters
        square
        sx={sectionAccordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
          <Typography sx={sectionTitleSx}>Configurations</Typography>
          <Tooltip title="Add configuration" arrow>
            <IconButton
              data-cy="system-add-config-btn"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                createConfig();
                onNavigate?.();
              }}
              sx={{
                ml: 1,
                color: "text.secondary",
                borderRadius: "8px",
                "&:hover": {
                  color: accent,
                  backgroundColor: alpha(accent, 0.1),
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, pb: 1 }}>
          {configs.map((cfg: any) => {
            const isDefault = cfg.clientProcess?.is_default || cfg.is_default;
            const configKey = cfg.configKey || cfg.name;
            const displayName = cfg.clientProcess?.name || cfg.name || configKey;
            const isExpanded = expandedConfigId === configKey;

            return (
              <Box key={configKey} sx={{ px: 1.25, py: 0.25 }}>
                <Accordion
                  data-cy={`system-nav-config-${configKey}`}
                  expanded={isExpanded}
                  onChange={() => selectConfig(configKey)}
                  disableGutters
                  square
                  sx={{
                    backgroundColor: isExpanded
                      ? alpha(accent, isDark ? 0.1 : 0.06)
                      : "transparent",
                    boxShadow: "none",
                    borderRadius: "8px",
                    border: `1px solid ${
                      isExpanded
                        ? alpha(accent, isDark ? 0.35 : 0.28)
                        : "transparent"
                    }`,
                    transition: "background-color 140ms ease, border-color 140ms ease",
                    "&:before": { display: "none" },
                    "&:hover": {
                      backgroundColor: isExpanded
                        ? alpha(accent, isDark ? 0.14 : 0.08)
                        : alpha(isDark ? "#fff" : "#000", 0.04),
                    },
                    m: 0,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
                    sx={{
                      px: 1.5,
                      minHeight: 40,
                      "& .MuiAccordionSummary-content": {
                        minWidth: 0,
                        my: 0.75,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        minWidth: 0,
                        pr: 0.5,
                      }}
                    >
                      <Tooltip title={displayName} arrow placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                            minWidth: 0,
                            fontWeight: isExpanded ? 600 : 500,
                            color: isExpanded ? "text.primary" : "text.secondary",
                          }}
                        >
                          {displayName}
                        </Typography>
                      </Tooltip>

                      {isDefault && (
                        <Tooltip title="Default configuration" arrow>
                          <StarIcon
                            sx={{ ml: 1, flexShrink: 0, fontSize: 14, color: accent }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0, pt: 0.25, pb: 0.75 }}>
                    <List disablePadding>
                      {tabOptions.map((section) => {
                        const selected = isExpanded && selectedSection === section;
                        return (
                          <ListItemButton
                            data-cy={`system-nav-config-section-${section}`}
                            key={section}
                            selected={selected}
                            onClick={() => {
                              setSelectedSection(section);
                              onNavigate?.();
                            }}
                            sx={{
                              ...navItemSx(selected),
                              mx: 0.5,
                              pl: 2.25,
                            }}
                          >
                            <ListItemText
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: navItemTextSx(selected),
                              }}
                              primary={sectionLabel[section] || section}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionPanel;
